import { useEffect, useState } from 'react';
import {
  addressComponentLabel,
  addressComponentRequired,
  CONTACT_PRIMARY_KEY,
  isItemListField,
  offersPostcodeLookup,
  postcodeAddressLabel,
  postcodeAddressValue,
  primaryContactIndex,
  resolveAddressComponents,
  resolveRepeaterSubFields,
  resolveStaticOptions,
  type Country,
  type FieldDef,
  type FieldOption,
  type PostcodeAddress,
  type RepeaterItem,
  type RepeaterSubField,
} from '@jfh/intake-shared';
import { api } from '../lib/api.js';
import { Icon } from '../components/Icon.js';
import { PhoneField } from '../components/CountryPhone.js';
import { SearchSelect } from '../components/SearchSelect.js';
import { asInputValue, displayValue } from './fieldDisplay.js';

/** Customer field types editable in the portal (mirrors the Teams card). */
const EDITABLE = new Set([
  'short_text',
  'long_text',
  'email',
  'phone',
  'number',
  'vat',
  'date',
  'select',
  'radio',
  'address',
  'contact',
  'repeater',
]);

export function isApplicantEditable(f: FieldDef): boolean {
  return EDITABLE.has(f.type);
}

/**
 * Inline editor for the applicant's (customer) fields — the portal equivalent of
 * editing on the Teams card. Scalar/select/address fields become inputs, and
 * contact / repeater groups become a full add-remove-edit editor (with the
 * primary-contact picker); multi-select, consent, and file stay read-only. The
 * caller owns `values` and persists changed fields via amend.
 */
export function ApplicantEditor({
  fields,
  values,
  onChange,
  countries = [],
  defaultCountryIso = 'GB',
}: {
  fields: FieldDef[];
  values: Record<string, unknown>;
  onChange: (id: string, value: unknown) => void;
  countries?: Country[];
  defaultCountryIso?: string;
}) {
  return (
    <div style={{ display: 'grid', gap: 14, marginTop: 6 }}>
      {fields.map((f) => (
        <div key={f.id}>
          <label className="field-label">{f.label}</label>
          {isApplicantEditable(f) ? (
            <EditControl
              field={f}
              value={values[f.id]}
              onChange={(v) => onChange(f.id, v)}
              countries={countries}
              defaultCountryIso={defaultCountryIso}
            />
          ) : (
            <div style={{ fontSize: 13.5, color: 'var(--text-2)' }}>
              {displayValue(values[f.id]) || '—'}{' '}
              <span style={{ fontSize: 11, color: 'var(--text-3)' }}>(not editable here)</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function EditControl({
  field: f,
  value,
  onChange,
  countries,
  defaultCountryIso,
}: {
  field: FieldDef;
  value: unknown;
  onChange: (value: unknown) => void;
  countries: Country[];
  defaultCountryIso: string;
}) {
  if (isItemListField(f.type)) {
    return (
      <ContactEditor
        field={f}
        value={value}
        onChange={onChange}
        countries={countries}
        defaultCountryIso={defaultCountryIso}
      />
    );
  }
  if (f.type === 'address') {
    return <AddressEdit field={f} value={value} onChange={onChange} />;
  }
  if (f.type === 'phone') {
    return (
      <PhoneField
        value={asInputValue(value)}
        onChange={onChange}
        countries={countries}
        defaultIso={defaultCountryIso}
        placeholder={f.placeholder ?? f.label}
      />
    );
  }
  if (f.type === 'select' || f.type === 'radio') {
    return <OptionSelect field={f} value={asInputValue(value)} onChange={onChange} />;
  }
  if (f.type === 'long_text') {
    return (
      <textarea
        className="textarea"
        rows={2}
        value={asInputValue(value)}
        placeholder={f.placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  return (
    <input
      className="input"
      type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
      value={asInputValue(value)}
      placeholder={f.placeholder ?? f.label}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function AddressEdit({
  field: f,
  value,
  onChange,
}: {
  field: FieldDef;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const obj =
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  const components = resolveAddressComponents(f).filter((c) => c.enabled);
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {offersPostcodeLookup(f.address) && (
        <FindByPostcode
          initial={typeof obj.postcode === 'string' ? obj.postcode : ''}
          onPick={(a) => onChange({ ...obj, ...postcodeAddressValue(a, f.address) })}
        />
      )}
      {components.map((c) => (
        <input
          key={c.key}
          className="input"
          placeholder={addressComponentLabel(c) + (addressComponentRequired(c, obj) ? ' *' : '')}
          aria-label={addressComponentLabel(c)}
          value={typeof obj[c.key] === 'string' ? (obj[c.key] as string) : ''}
          onChange={(e) => onChange({ ...obj, [c.key]: e.target.value })}
        />
      ))}
    </div>
  );
}

/** Staff side of the postcode look-up: find, pick, and the lines below fill in. */
function FindByPostcode({ initial, onPick }: { initial: string; onPick: (a: PostcodeAddress) => void }) {
  const [postcode, setPostcode] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [found, setFound] = useState<PostcodeAddress[] | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const find = async () => {
    setBusy(true);
    setMessage(null);
    const r = await api.lookupPostcode(postcode);
    setBusy(false);
    if (r.kind === 'found') {
      setFound(r.addresses);
      return;
    }
    setFound(null);
    setMessage(
      r.kind === 'invalid'
        ? 'Enter a full UK postcode.'
        : r.kind === 'not_found'
          ? 'No addresses found for that postcode.'
          : r.kind === 'busy'
            ? 'Too many look-ups. Wait a minute and try again.'
            : 'Address look-up is unavailable just now.',
    );
  };
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          className="input"
          style={{ maxWidth: 140 }}
          value={postcode}
          placeholder="Postcode"
          aria-label="Find by postcode"
          onChange={(e) => setPostcode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void find();
            }
          }}
        />
        <button type="button" className="btn sm" onClick={() => void find()} disabled={busy || !postcode.trim()}>
          <Icon name="search" size={13} /> {busy ? 'Finding…' : 'Find address'}
        </button>
      </div>
      {found && (
        <select
          className="select"
          aria-label="Choose the address"
          defaultValue=""
          onChange={(e) => {
            const a = found[Number(e.target.value)];
            if (a) onPick(a);
          }}
        >
          <option value="" disabled>
            Choose the address ({found.length} found)
          </option>
          {found.map((a, i) => (
            <option key={i} value={i}>
              {postcodeAddressLabel(a)}
            </option>
          ))}
        </select>
      )}
      {message && <div className="field-help" style={{ marginTop: 0, color: 'var(--st-declined)' }}>{message}</div>}
    </div>
  );
}

/** Adapt a repeater/contact sub-field to a FieldDef so EditControl can render it. */
function subFieldToFieldDef(s: RepeaterSubField): FieldDef {
  return {
    id: s.id,
    type: s.type,
    label: s.label,
    audience: 'customer',
    required: s.required,
    placeholder: s.placeholder,
    options: s.options,
    address: s.address,
  };
}

/**
 * Editor for a contact / repeater field: each item is a card of its sub-field
 * inputs (reusing EditControl), with add / remove and — for contacts — a primary
 * picker. Emits the full item array (preserving the `_primary` meta key) so the
 * caller's JSON diff and the iQ `Contacts[]` mapping work unchanged.
 */
function ContactEditor({
  field: f,
  value,
  onChange,
  countries,
  defaultCountryIso,
}: {
  field: FieldDef;
  value: unknown;
  onChange: (value: unknown) => void;
  countries: Country[];
  defaultCountryIso: string;
}) {
  const subs = resolveRepeaterSubFields(f);
  const isContact = f.type === 'contact';
  const items: RepeaterItem[] = Array.isArray(value)
    ? (value.filter(
        (it) => it !== null && typeof it === 'object' && !Array.isArray(it),
      ) as RepeaterItem[])
    : [];
  const itemLabel = f.itemLabel?.trim() || (isContact ? 'Contact' : 'Item');
  const primaryIdx = isContact ? primaryContactIndex(items) : -1;

  const setItemField = (i: number, subId: string, v: unknown) =>
    onChange(items.map((it, j) => (j === i ? { ...it, [subId]: v } : it)));
  const setPrimary = (i: number) =>
    onChange(items.map((it, j) => ({ ...it, [CONTACT_PRIMARY_KEY]: j === i })));
  const addItem = () =>
    onChange([...items, isContact && items.length === 0 ? { [CONTACT_PRIMARY_KEY]: true } : {}]);
  const removeItem = (i: number) => onChange(items.filter((_, j) => j !== i));

  return (
    <div style={{ display: 'grid', gap: 10, marginTop: 4 }}>
      {items.map((item, i) => (
        <div key={i} className="card" style={{ padding: 12, background: 'var(--bg-app)' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              marginBottom: 8,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-3)' }}>
              {itemLabel} {i + 1}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {isContact && (
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    fontSize: 12,
                    color: 'var(--text-2)',
                  }}
                >
                  <input
                    type="radio"
                    name={`${f.id}-primary`}
                    checked={i === primaryIdx}
                    onChange={() => setPrimary(i)}
                  />
                  Primary
                </label>
              )}
              <button
                className="btn sm"
                aria-label={`Remove ${itemLabel.toLowerCase()} ${i + 1}`}
                style={{ height: 28, width: 28, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                onClick={() => removeItem(i)}
              >
                <Icon name="trash" size={13} style={{ color: 'var(--text-3)' }} />
              </button>
            </div>
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            {subs.map((s) => (
              <div key={s.id}>
                <label className="field-label">
                  {s.label}
                  {s.required ? ' *' : ''}
                </label>
                <EditControl
                  field={subFieldToFieldDef(s)}
                  value={item[s.id]}
                  onChange={(v) => setItemField(i, s.id, v)}
                  countries={countries}
                  defaultCountryIso={defaultCountryIso}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
      <button
        className="btn sm"
        style={{ justifySelf: 'start', display: 'inline-flex', alignItems: 'center', gap: 6 }}
        onClick={addItem}
      >
        <Icon name="plus" size={13} style={{ color: 'var(--accent)' }} /> Add {itemLabel.toLowerCase()}
      </button>
    </div>
  );
}

/** Select/radio dropdown, resolving iQ-backed option lists when configured. */
function OptionSelect({
  field: f,
  value,
  onChange,
}: {
  field: FieldDef;
  value: string;
  onChange: (value: string) => void;
}) {
  const iqKey = f.optionsSource?.type === 'iq' ? f.optionsSource.key : null;
  const [iqOptions, setIqOptions] = useState<FieldOption[] | null>(null);

  useEffect(() => {
    if (!iqKey) return;
    let cancelled = false;
    api
      .getLookup(iqKey)
      .then((r) => !cancelled && setIqOptions(r.options))
      .catch(() => !cancelled && setIqOptions([]));
    return () => {
      cancelled = true;
    };
  }, [iqKey]);

  const options = iqOptions && iqOptions.length ? iqOptions : resolveStaticOptions(f);
  if (iqKey) {
    return (
      <SearchSelect
        value={value}
        options={options}
        onChange={onChange}
        placeholder={f.placeholder ?? 'Select…'}
      />
    );
  }
  return (
    <select className="select" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{f.placeholder ?? 'Select…'}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
